import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDateNumeric, formatDateTimeShort } from '@/lib/dates';
import { useState } from 'react';
import {
  ChevronRight,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Eye,
  X,
  Clock,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useDeadLetterQueue,
  ActionButton,
  JsonViewer,
  type DeadLetterQueueItem,
} from '@/components/hermes';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/hermes/dlq')({
  component: DeadLetterQueuePage,
});

function DeadLetterQueuePage() {
  const [selectedItem, setSelectedItem] = useState<DeadLetterQueueItem | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const { data: dlqItems, isLoading, refetch } = useDeadLetterQueue();
  const queryClient = useQueryClient();

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('pipeline_dead_letter_queue')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Failed to delete item');
      return;
    }

    toast.success('Item deleted');
    queryClient.invalidateQueries({ queryKey: ['dead-letter-queue'] });
    if (selectedItem?.id === id) {
      setSelectedItem(null);
    }
  };

  const handlePurgeAll = async () => {
    setShowPurgeConfirm(false);
    if (!dlqItems || dlqItems.length === 0) return;

    const { error } = await supabase
      .from('pipeline_dead_letter_queue')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (error) {
      toast.error('Failed to purge queue');
      return;
    }

    toast.success('Queue purged');
    queryClient.invalidateQueries({ queryKey: ['dead-letter-queue'] });
    setSelectedItem(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to="/hermes" className="hover:text-foreground">Hermes</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Dead Letter Queue</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            Dead Letter Queue
            {dlqItems && dlqItems.length > 0 && (
              <span className="text-sm px-2.5 py-1 rounded-full bg-red-500/20 text-red-400 font-normal">
                {dlqItems.length} items
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton onClick={async () => { await refetch(); }} size="sm" variant="ghost">
            <RefreshCw className="w-3.5 h-3.5" />
          </ActionButton>
          {dlqItems && dlqItems.length > 0 && (
            <ActionButton onClick={() => setShowPurgeConfirm(true)} size="sm" variant="destructive">
              <Trash2 className="w-3.5 h-3.5" />
              Purge All
            </ActionButton>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-secondary/50">
              <h2 className="font-semibold text-foreground">Failed Tasks</h2>
            </div>

            {isLoading ? (
              <div className="px-4 py-8 text-center text-muted-foreground">
                Loading...
              </div>
            ) : dlqItems?.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-emerald-500" />
                </div>
                <p className="text-foreground font-medium">Queue is empty</p>
                <p className="text-sm text-muted-foreground mt-1">
                  No failed tasks in the dead letter queue
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {dlqItems?.map((item) => (
                  <div
                    key={item.id}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      selectedItem?.id === item.id ? 'bg-primary/10' : 'hover:bg-secondary/30'
                    }`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <span className="font-medium text-foreground truncate">
                            {getTaskName(item.original_message)}
                          </span>
                        </div>
                        <p className="text-xs text-red-400 truncate">
                          {item.error_message}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>Failed {formatRelativeTime(item.failed_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                          }}
                          className="p-1.5 rounded hover:bg-secondary transition-colors"
                          title="View details"
                        >
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          className="p-1.5 rounded hover:bg-red-500/20 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div>
          {selectedItem ? (
            <DLQItemDetail item={selectedItem} onClose={() => setSelectedItem(null)} />
          ) : (
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-sm text-muted-foreground text-center">
                Select an item to view details
              </p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Purge all items?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete all {dlqItems?.length || 0} items from the dead letter queue? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePurgeAll}>Purge All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface DLQItemDetailProps {
  item: DeadLetterQueueItem;
  onClose: () => void;
}

function DLQItemDetail({ item, onClose }: DLQItemDetailProps) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-secondary/50 flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Item Details</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-secondary transition-colors"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Error Message */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Error Message
          </h4>
          <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20">
            <p className="text-sm text-red-400 font-mono whitespace-pre-wrap">
              {item.error_message}
            </p>
          </div>
        </div>

        {/* Failed At */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Failed At
          </h4>
          <p className="text-sm text-foreground">
            {formatDateTimeShort(item.failed_at)}
          </p>
        </div>

        {/* Original Message */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Original Message
          </h4>
          <JsonViewer
            data={item.original_message}
            defaultExpanded={true}
            maxHeight="250px"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <button
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function getTaskName(message: Record<string, unknown>): string {
  if (typeof message.task_name === 'string') return message.task_name;
  if (typeof message.taskName === 'string') return message.taskName;
  if (typeof message.name === 'string') return message.name;
  return 'Unknown Task';
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateNumeric(date);
}
